import React, { useState } from 'react';
import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material';
import { saveUser } from '../../services/users.js';
import logoWhite from '../../assets/images/logo-white.png';
import Input from '../../components/Input';
import InputPassword from '../../components/Input/inputPassword.js';
import { AlertError, AlertSuccess } from '../../components/Alerts';
import { useAlert } from '../../context';
import './index.scss';

const concessionTypeOptions = [
  'Municipal Autônoma', 'Autarquia', 'Empresa Pública',
  'Concessionária Privada', 'PPP', 'Outros',
];

const RegisterForm = () => {
  const initialState = {
    name: '', email: '', cpf: '', role: 'common', image_url: '',
    phone: '', city: '', uf: '', mobile_operator_name: '',
    concession_type: '', company: '', position: '',
    password_confirm: '', password: '',
    is_admin: false, blocked: false, premium: false, errorPassword: false,
  };

  const [form, setForm] = useState(initialState);
  const [concessionFocused, setConcessionFocused] = useState(false);
  const { setShowSuccess, setShowError } = useAlert();

  const set = field => e => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleRegister = e => {
    e.preventDefault();
    if (!form.password || !form.password_confirm || form.password !== form.password_confirm) {
      setForm(prev => ({ ...prev, errorPassword: true }));
      return;
    }
    setForm(prev => ({ ...prev, errorPassword: false }));
    saveUser(form).then(
      () => { setShowSuccess(true); setTimeout(() => (window.location.href = '/'), 3000); },
      () => setShowError(true)
    );
  };

  return (
    <Box className="auth-layout">
      {/* Painel esquerdo */}
      <Box className="auth-panel-left">
        <img src={logoWhite} alt="HWater" />
        <Typography className="auth-panel-tagline">
          Biblioteca Virtual de Saneamento
        </Typography>
        <Typography className="auth-panel-sub">
          Crie sua conta para acessar documentos, normas e publicações relevantes para o setor de saneamento.
        </Typography>
      </Box>

      {/* Painel direito com scroll */}
      <Box className="auth-panel-right">
        <Box
          className="auth-form-inner"
          component="form"
          onSubmit={handleRegister}
          sx={{ maxHeight: { xs: 'none', sm: 'calc(100vh - 80px)' }, overflowY: { xs: 'visible', sm: 'auto' }, pr: { sm: '4px' } }}
        >
          <Typography className="auth-title">Criar conta</Typography>
          <Typography className="auth-subtitle">Preencha seus dados para solicitar acesso</Typography>

          {/* Dados pessoais */}
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '8px', mt: '4px' }}>
            Dados pessoais
          </Typography>
          <Box sx={{ mb: '10px' }}><Input required type="text" label="Nome" name="name" value={form.name} onChange={set('name')} /></Box>
          <Box sx={{ mb: '10px' }}><Input required type="email" label="Email" name="email" value={form.email} onChange={set('email')} /></Box>
          <Box className="auth-field-row" sx={{ mb: '10px' }}>
            <Input type="phone" label="Telefone" name="phone" value={form.phone} onChange={set('phone')} />
            <Input type="text" label="CPF" name="cpf" value={form.cpf} onChange={set('cpf')} />
          </Box>

          {/* Localização */}
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '8px', mt: '8px' }}>
            Localização
          </Typography>
          <Box className="auth-field-row" sx={{ mb: '10px' }}>
            <Input type="text" label="Cidade" name="city" value={form.city} onChange={set('city')} />
            <Box sx={{ maxWidth: '80px' }}>
              <Input type="text" label="UF" name="uf" maxLength={2} value={form.uf} onChange={e => setForm(prev => ({ ...prev, uf: e.target.value.toUpperCase() }))} />
            </Box>
          </Box>

          {/* Empresa */}
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '8px', mt: '8px' }}>
            Empresa
          </Typography>
          <Box className="auth-field-row" sx={{ mb: '10px' }}>
            <Input type="text" label="Empresa" name="company" value={form.company} onChange={set('company')} />
            <Input type="text" label="Cargo" name="position" value={form.position} onChange={set('position')} />
          </Box>
          <Box sx={{ mb: '10px' }}>
            <div className="material-textfield">
              <select
                value={form.concession_type}
                onChange={e => setForm(prev => ({ ...prev, concession_type: e.target.value }))}
                onFocus={() => setConcessionFocused(true)}
                onBlur={() => setConcessionFocused(false)}
                style={{
                  width: '100%', fontSize: '1rem', fontWeight: 'normal', outline: 'none',
                  border: '1px solid #6d6e71', borderRadius: '5px', padding: '0.71rem 0.7rem',
                  color: '#001f27', backgroundColor: 'white', cursor: 'pointer', marginBottom: 0,
                }}
              >
                <option value=""></option>
                {concessionTypeOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              <label style={{
                top: concessionFocused || form.concession_type ? '0' : '35%',
                transform: concessionFocused || form.concession_type ? 'translateY(-50%) scale(0.9)' : 'translateY(-50%)',
              }}>
                Tipo de concessão
              </label>
            </div>
          </Box>
          <Box sx={{ mb: '10px' }}>
            <Input type="text" label="Operadora do celular" name="mobile_operator_name" value={form.mobile_operator_name} onChange={set('mobile_operator_name')} />
          </Box>

          {/* Senha */}
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '8px', mt: '8px' }}>
            Senha
          </Typography>
          <Box className="auth-field-row" sx={{ mb: '8px' }}>
            <InputPassword label="Senha" name="password" value={form.password} onChange={set('password')} />
            <InputPassword label="Confirmar senha" name="confirm-password" value={form.password_confirm} onChange={set('password_confirm')} />
          </Box>

          {form.errorPassword && (
            <Stack sx={{ width: '100%', mb: '8px' }}>
              <Alert severity="error">As senhas informadas são diferentes.</Alert>
            </Stack>
          )}

          <Button type="submit" variant="contained" className="auth-btn" onClick={handleRegister}>
            Criar conta
          </Button>

          <Box className="auth-links">
            <span>Já tem conta? <Link href="/">Entrar</Link></span>
          </Box>
        </Box>

        <AlertError textAlert="Este e-mail já está cadastrado." />
        <AlertSuccess textAlert="Conta criada com sucesso! Redirecionando..." />
      </Box>
    </Box>
  );
};

export default RegisterForm;
